import React, { useEffect, useState } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { monitorApi } from '../../services/api';
import {
  INTERVAL_OPTIONS,
  HTTP_METHODS,
  ENVIRONMENT_TAGS,
  BU_TAGS,
  getMinIntervalForEnvironment,
} from '../../config/appConfig';
import { Monitor, MonitorGroup, Recipient } from '../../types';

interface Props {
  monitor: Monitor | null;
  duplicateFrom?: Monitor | null;
  monitors: Monitor[];
  groups: MonitorGroup[];
  recipients: Recipient[];
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

const emptyForm = {
  name: '',
  url: '',
  method: 'GET',
  headers: '',
  body: '',
  group_id: '' as string | number,
  tags: 'Production',
  interval_seconds: 300,
  timeout_seconds: 30,
  expected_status_code: 200,
  follow_redirects: true,
  verify_ssl: true,
  active: true,
};

const parseTagArray = (raw: string | null | undefined): string[] =>
  Array.from(new Set((raw || '').split(',').map(t => t.trim()).filter(Boolean)));

const toTagString = (tags: string[]): string => Array.from(new Set(tags)).join(',');

type HeaderEntry = { id: string; key: string; value: string };

const parseHeaderEntries = (raw: string | null | undefined): HeaderEntry[] => {
  const input = (raw || '').trim();
  if (!input) return [{ id: 'h1', key: '', value: '' }];

  if (input.startsWith('{')) {
    try {
      const obj = JSON.parse(input) as Record<string, unknown>;
      const entries = Object.entries(obj).map(([k, v], idx) => ({ id: `h${idx + 1}`, key: k, value: String(v ?? '') }));
      return entries.length > 0 ? entries : [{ id: 'h1', key: '', value: '' }];
    } catch {
      // Fall through to line parsing
    }
  }

  const lineEntries = input
    .split('\n')
    .map((line, idx) => {
      const i = line.indexOf(':');
      if (i <= 0) return null;
      return {
        id: `h${idx + 1}`,
        key: line.slice(0, i).trim(),
        value: line.slice(i + 1).trim(),
      };
    })
    .filter(Boolean) as HeaderEntry[];

  return lineEntries.length > 0 ? lineEntries : [{ id: 'h1', key: '', value: '' }];
};

const toHeaderString = (entries: HeaderEntry[]): string =>
  entries
    .map(e => ({ key: e.key.trim(), value: e.value.trim() }))
    .filter(e => e.key && e.value)
    .map(e => `${e.key}: ${e.value}`)
    .join('\n');

const parseHeaderMap = (raw: string | null | undefined): Record<string, string> => {
  const input = (raw || '').trim();
  if (!input) return {};
  if (input.startsWith('{')) {
    try {
      const obj = JSON.parse(input) as Record<string, unknown>;
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '')]));
    } catch {
      // Fall through to line parsing
    }
  }
  const out: Record<string, string> = {};
  input.split('\n').forEach(line => {
    const i = line.indexOf(':');
    if (i <= 0) return;
    const key = line.slice(0, i).trim();
    const value = line.slice(i + 1).trim();
    if (key) out[key] = value;
  });
  return out;
};

const MonitorFormModal: React.FC<Props> = ({ monitor, duplicateFrom, monitors, groups, recipients, onClose, onSaved, onError }) => {
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedRecipients, setSelectedRecipients] = useState<number[]>([]);
  const [headerEntries, setHeaderEntries] = useState<HeaderEntry[]>([{ id: 'h1', key: '', value: '' }]);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string>('');

  const selectedTags = parseTagArray(form.tags);
  const selectedEnvironment = selectedTags.find(tag => ENVIRONMENT_TAGS.includes(tag as any)) || 'Production';
  const minInterval = getMinIntervalForEnvironment(selectedEnvironment);
  const allowedIntervals = INTERVAL_OPTIONS.filter(o => o.value >= minInterval);
  const selectedGroup = groups.find(g => String(g.id) === String(form.group_id));
  const isDcxGroup = !!selectedGroup && selectedGroup.name.trim().toLowerCase() === 'dcx';
  const inheritedGroupHeaders = selectedGroup?.headers || '';
  const inheritedHeaderMap = parseHeaderMap(inheritedGroupHeaders);
  const monitorHeaderMap = parseHeaderMap(toHeaderString(headerEntries));
  const effectiveHeaderMap = { ...inheritedHeaderMap, ...monitorHeaderMap };
  const effectiveHeaderEntries = Object.entries(effectiveHeaderMap);

  useEffect(() => {
    const source = monitor || duplicateFrom;
    if (source) {
      const existingTags = parseTagArray(source.tags || '');
      const existingEnvironment = existingTags.find(tag => ENVIRONMENT_TAGS.includes(tag as any));
      const normalizedTags = existingEnvironment ? existingTags : ['Production', ...existingTags.filter(t => !ENVIRONMENT_TAGS.includes(t as any))];
      const nextMinInterval = getMinIntervalForEnvironment(existingEnvironment || 'Production');
      setForm({
        name: duplicateFrom ? `${source.name} (Copy)` : source.name,
        url: source.url,
        method: source.method,
        headers: source.headers || '',
        body: source.body || '',
        group_id: source.group_id ?? '',
        tags: toTagString(normalizedTags),
        interval_seconds: Math.max(source.interval_seconds, nextMinInterval),
        timeout_seconds: source.timeout_seconds ?? 30,
        expected_status_code: source.expected_status_code ?? 200,
        follow_redirects: !!source.follow_redirects,
        verify_ssl: !!source.verify_ssl,
        active: source.active,
      });
      setHeaderEntries(parseHeaderEntries(source.headers || ''));
      monitorApi.getMonitorRecipients(source.id).then(res => setSelectedRecipients(res.data)).catch(() => {});
    } else {
      setForm({ ...emptyForm });
      setHeaderEntries([{ id: 'h1', key: '', value: '' }]);
      setSelectedRecipients([]);
    }
  }, [monitor, duplicateFrom]);

  useEffect(() => {
    if (monitor || duplicateFrom) return;
    const selected = groups.find(g => String(g.id) === String(form.group_id));
    if (!selected) {
      setSelectedRecipients([]);
      return;
    }
    setSelectedRecipients((selected.default_recipient_ids || []).map(Number));
  }, [form.group_id, groups, monitor, duplicateFrom]);

  useEffect(() => {
    if (form.interval_seconds < minInterval) {
      setForm(prev => ({ ...prev, interval_seconds: minInterval }));
    }
  }, [form.interval_seconds, minInterval]);

  useEffect(() => {
    if (isDcxGroup) return;
    const current = parseTagArray(form.tags);
    const next = current.filter(tag => !BU_TAGS.includes(tag as any));
    if (next.length !== current.length) {
      setForm(prev => ({ ...prev, tags: toTagString(next) }));
    }
  }, [isDcxGroup, form.tags]);

  const set = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    const normalizedUrl = String(form.url || '').trim().toLowerCase();
    const duplicate = monitors.find(m =>
      String(m.url || '').trim().toLowerCase() === normalizedUrl && (!monitor || m.id !== monitor.id)
    );
    if (duplicate) {
      setModalError(`A monitor with this URL already exists (${duplicate.name}).`);
      return;
    }

    setSaving(true);

    const normalizedTags = parseTagArray(form.tags);
    const finalTags = isDcxGroup
      ? normalizedTags
      : normalizedTags.filter(tag => !BU_TAGS.includes(tag as any));

    const payload: Partial<Monitor> = {
      ...form,
      url: String(form.url || '').trim(),
      headers: toHeaderString(headerEntries),
      tags: toTagString(finalTags),
      group_id: form.group_id === '' ? null : Number(form.group_id),
    } as any;
    try {
      let monitorId: number;
      if (monitor) {
        await monitorApi.updateMonitor(monitor.id, payload);
        monitorId = monitor.id;
      } else {
        const res = await monitorApi.createMonitor({ ...payload, recipient_ids: selectedRecipients });
        monitorId = res.data.id;
      }
      if (monitor) {
        await monitorApi.setMonitorRecipients(monitorId, selectedRecipients);
      }
      onSaved(monitor ? 'Monitor updated' : 'Monitor created');
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to save monitor';
      setModalError(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleRecipient = (id: number) =>
    setSelectedRecipients(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const setEnvironmentTag = (envTag: string) => {
    const current = parseTagArray(form.tags);
    const nonEnv = current.filter(tag => !ENVIRONMENT_TAGS.includes(tag as any));
    set('tags', toTagString([envTag, ...nonEnv]));
  };

  const toggleBuTag = (buTag: string) => {
    const current = parseTagArray(form.tags);
    const has = current.includes(buTag);
    const next = has ? current.filter(tag => tag !== buTag) : [...current, buTag];
    set('tags', toTagString(next));
  };

  const addHeaderEntry = () => {
    setHeaderEntries(prev => [...prev, { id: `h${Date.now()}-${prev.length + 1}`, key: '', value: '' }]);
  };

  const removeHeaderEntry = (id: string) => {
    setHeaderEntries(prev => {
      const next = prev.filter(h => h.id !== id);
      return next.length > 0 ? next : [{ id: 'h1', key: '', value: '' }];
    });
  };

  const updateHeaderEntry = (id: string, field: 'key' | 'value', value: string) => {
    setHeaderEntries(prev => prev.map(h => (h.id === id ? { ...h, [field]: value } : h)));
  };

  const input = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40';
  const label = 'block text-sm font-medium text-slate-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        {modalError && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto max-w-md w-full bg-red-50 border border-red-300 text-red-800 rounded-lg shadow-lg px-4 py-3 text-sm text-center">
              <div className="font-medium">Unable to save monitor</div>
              <div className="mt-1">{modalError}</div>
              <button
                type="button"
                onClick={() => setModalError('')}
                className="mt-3 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded hover:bg-red-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-slate-800">
            {monitor ? 'Edit Monitor' : duplicateFrom ? `Duplicate Monitor: ${duplicateFrom.name}` : 'New Monitor'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className={label}>Name *</label>
            <input className={input} value={form.name} onChange={e => set('name', e.target.value)} required maxLength={200} />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className={label}>Method</label>
              <select className={input} value={form.method} onChange={e => set('method', e.target.value)}>
                {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-3">
              <label className={label}>URL *</label>
              <input className={input} value={form.url} onChange={e => set('url', e.target.value)} required placeholder="https://example.com/health" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Group</label>
              <select className={input} value={form.group_id} onChange={e => set('group_id', e.target.value)}>
                <option value="">Ungrouped</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Tags *</label>
              <div className="border border-slate-300 rounded-md p-3 space-y-3">
                <div>
                  <div className="text-xs text-slate-500 mb-2">Environment (required, single select)</div>
                  <div className="flex flex-wrap gap-3">
                    {ENVIRONMENT_TAGS.map(tag => (
                      <label key={tag} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="radio"
                          name="environment-tag"
                          checked={selectedEnvironment === tag}
                          onChange={() => setEnvironmentTag(tag)}
                        />
                        {tag}
                      </label>
                    ))}
                  </div>
                </div>
                {isDcxGroup && (
                  <div>
                    <div className="text-xs text-slate-500 mb-2">Business Unit (optional, multi-select)</div>
                    <div className="flex flex-wrap gap-3">
                      {BU_TAGS.map(tag => (
                        <label key={tag} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={selectedTags.includes(tag)}
                            onChange={() => toggleBuTag(tag)}
                          />
                          {tag}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className={label}>Request Headers</label>
            {(selectedGroup?.headers || '').trim() && (
              <div className="border border-slate-200 rounded-md p-3 mb-2 bg-slate-50">
                <div className="text-xs font-medium text-slate-600 mb-1">Inherited from group: {selectedGroup?.name}</div>
                <pre className="text-[11px] text-slate-600 font-mono whitespace-pre-wrap break-words">{selectedGroup?.headers}</pre>
              </div>
            )}
            <div className="border border-slate-300 rounded-md p-3 space-y-2">
              {headerEntries.map((entry, idx) => (
                <div key={entry.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <input
                    className={`${input} font-mono text-xs`}
                    placeholder="Header name"
                    value={entry.key}
                    onChange={e => updateHeaderEntry(entry.id, 'key', e.target.value)}
                  />
                  <input
                    className={`${input} font-mono text-xs`}
                    placeholder="Header value"
                    value={entry.value}
                    onChange={e => updateHeaderEntry(entry.id, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeHeaderEntry(entry.id)}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded"
                    title={`Remove header ${idx + 1}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addHeaderEntry}
                className="inline-flex items-center gap-2 text-xs font-medium text-brand hover:opacity-80"
              >
                <Plus size={14} /> Add header
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Headers are sent as <code>Key: Value</code> entries. Monitor-level headers override inherited group headers when keys match.</p>
            {effectiveHeaderEntries.length > 0 && (
              <div className="mt-2 border border-slate-200 rounded-md p-3 bg-white">
                <div className="text-xs font-medium text-slate-600 mb-1">Effective headers sent with checks</div>
                <pre className="text-[11px] text-slate-600 font-mono whitespace-pre-wrap break-words">
                  {effectiveHeaderEntries.map(([k, v]) => `${k}: ${v}`).join('\n')}
                </pre>
              </div>
            )}
          </div>

          {!['GET', 'HEAD'].includes(form.method) && (
            <div>
              <label className={label}>Request Body</label>
              <textarea className={`${input} font-mono text-xs`} rows={3} value={form.body} onChange={e => set('body', e.target.value)} placeholder='{"key": "value"}' />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={label}>Check Interval *</label>
              <select className={input} value={form.interval_seconds} onChange={e => set('interval_seconds', Number(e.target.value))}>
                {allowedIntervals.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Minimum for {selectedEnvironment}: {INTERVAL_OPTIONS.find(o => o.value === minInterval)?.label || `${minInterval}s`}.
              </p>
            </div>
            <div>
              <label className={label}>Timeout (s)</label>
              <input type="number" min={1} max={120} className={input} value={form.timeout_seconds} onChange={e => set('timeout_seconds', Number(e.target.value))} />
            </div>
            <div>
              <label className={label}>Expected Status</label>
              <input type="number" min={100} max={599} className={input} value={form.expected_status_code} onChange={e => set('expected_status_code', Number(e.target.value))} />
            </div>
          </div>

          <div className="flex flex-wrap gap-5 pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.follow_redirects} onChange={e => set('follow_redirects', e.target.checked)} /> Follow redirects
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.verify_ssl} onChange={e => set('verify_ssl', e.target.checked)} /> Verify SSL
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} /> Active
            </label>
          </div>

          <div>
            <label className={label}>Additional Alert Recipients</label>
            <div className="border border-slate-200 rounded-md max-h-32 overflow-auto p-2 space-y-1">
              {recipients.length === 0 ? (
                <p className="text-xs text-slate-400 px-1 py-2">No recipients yet. Global &amp; group recipients still receive alerts automatically.</p>
              ) : recipients.map(r => (
                <label key={r.id} className="flex items-center gap-2 text-sm text-slate-600 px-1 py-0.5">
                  <input type="checkbox" checked={selectedRecipients.includes(r.id)} onChange={() => toggleRecipient(r.id)} />
                  {r.email} <span className="text-xs text-slate-400">({r.scope}{r.group_name ? `: ${r.group_name}` : ''})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand-dark disabled:opacity-60">
              <Save size={16} /> {saving ? 'Saving…' : 'Save Monitor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MonitorFormModal;
